/* global axios */
import ApiClient from './ApiClient';

class AppointmentsAPI extends ApiClient {
  constructor() {
    super('appointments', { accountScoped: true });
  }

  list(params, accountId, config = {}) {
    return axios.get(this.urlForAccount(accountId), {
      ...config,
      params: params || {},
    });
  }

  show(id, accountId, config = {}) {
    return axios.get(`${this.urlForAccount(accountId)}/${id}`, config);
  }

  create(data, accountId) {
    return axios.post(this.urlForAccount(accountId), {
      appointment: data || {},
    });
  }

  // NOTE: there is no hard-delete endpoint. The DELETE route soft-cancels the
  // appointment (controller#destroy authorizes :cancel? and runs
  // CancellationService). delete() is just cancel() without a reason; prefer
  // cancel() so the cancellation reason is recorded.
  delete(id, accountId) {
    return axios.delete(`${this.urlForAccount(accountId)}/${id}`);
  }

  // Cancel uses DELETE (the Rails destroy action, which soft-cancels) with an
  // optional reason param.
  cancel(id, data, accountId) {
    const params = data?.reason ? { reason: data.reason } : {};
    return axios.delete(`${this.urlForAccount(accountId)}/${id}`, { params });
  }

  // Routes define POST (not PATCH) for these status transitions
  confirm(id, accountId) {
    return axios.post(`${this.urlForAccount(accountId)}/${id}/confirm`);
  }

  complete(id, accountId) {
    return axios.post(`${this.urlForAccount(accountId)}/${id}/complete`);
  }

  noShow(id, accountId) {
    return axios.post(`${this.urlForAccount(accountId)}/${id}/no_show`);
  }

  update(id, data, accountId) {
    return axios.patch(`${this.urlForAccount(accountId)}/${id}`, {
      appointment: data || {},
    });
  }

  availability(params, accountId, config = {}) {
    return axios.get(`${this.urlForAccount(accountId)}/availability`, {
      ...config,
      params: params || {},
    });
  }

  // Same rule as `availability`, answered for a range of days in one request.
  // The agenda draws a week (7 days) or a month (up to 42) at a time; asking
  // day by day would be 7 to 42 round trips to paint one grid.
  // params: { professional_id, from: 'YYYY-MM-DD', to: 'YYYY-MM-DD', service_id?, duration_minutes? }
  availabilityRange(params, accountId, config = {}) {
    return axios.get(`${this.urlForAccount(accountId)}/availability_range`, {
      ...config,
      params: params || {},
    });
  }

  // Returns professionals available at a given slot (filters out: outside
  // working_hours, conflicting appointments — cancelled/no_show do not count).
  // params: { scheduled_at: ISO string, duration_minutes?: number, service_id?: number }
  availableProfessionals(params, accountId, config = {}) {
    return axios.get(
      `${this.urlForAccount(accountId)}/available_professionals`,
      { ...config, params: params || {} }
    );
  }

  // The route is `get 'export.csv', action: :export` and the OpenAPI contract
  // declares `/appointments/export.csv`. Calling `/export` without the suffix
  // fell through to `appointments#show` with `id: "export"`, so every CSV
  // download answered 422 "appointment_id deve ser um inteiro".
  exportCsv(params, accountId, config = {}) {
    return axios.get(`${this.urlForAccount(accountId)}/export.csv`, {
      ...config,
      params: params || {},
      responseType: 'blob',
    });
  }

  // Diretório de clientes: agregado no servidor sobre TODO o histórico. A tela
  // montava essa lista no cliente a partir de uma janela de ±6 meses, então
  // quem só tinha agendamento fora dela não aparecia nem era encontrado.
  clients(params, accountId, config = {}) {
    return axios.get(`${this.urlForAccount(accountId)}/clients`, {
      ...config,
      params: params || {},
    });
  }

  metrics(params, accountId, config = {}) {
    return axios.get(`${this.urlForAccount(accountId)}/metrics`, {
      ...config,
      params: params || {},
    });
  }

  contactHistory(accountId, contactId, config) {
    const collectionUrl = this.urlForAccount(accountId);
    const accountUrl = collectionUrl.slice(0, -`/${this.resource}`.length);
    const requestUrl = `${accountUrl}/contacts/${contactId}/appointment_history`;
    return config === undefined
      ? axios.get(requestUrl)
      : axios.get(requestUrl, config);
  }
}

export default new AppointmentsAPI();
